import { useState } from "react";
import { useLenis } from "lenis/react";
import { List, X, FlowerLotus } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

export default function Nav() {
  const lenis = useLenis();
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);

  const links = [
    { label: t.nav.about, href: "#about", testid: "nav-link-about" },
    { label: t.nav.practices, href: "#practices", testid: "nav-link-practices" },
    { label: t.nav.specialties, href: "#specialties", testid: "nav-link-specialties" },
    { label: t.nav.expat, href: "#expat-life", testid: "nav-link-expat-life" },
    { label: t.nav.sessions, href: "#sessions", testid: "nav-link-sessions" },
  ];

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (lenis) lenis.scrollTo(href, { offset: -70, duration: 1.4 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-forest/10 bg-cream/70 backdrop-blur-md" data-testid="main-nav">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a href="#top" onClick={go("#top")} className="flex items-center gap-2" data-testid="nav-logo">
          <FlowerLotus size={26} weight="duotone" className="text-moss" />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl italic tracking-tight">Heal &amp; Balance</span>
            <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.25em] text-stone">Cécile DC Kinnunen</span>
          </span>
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={l.testid}
              className="link-underline text-xs font-semibold uppercase tracking-[0.2em] text-moss transition-colors hover:text-forest"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Switch language / Changer de langue"
            className="flex items-center gap-1.5 rounded-full border border-forest/20 px-3.5 py-2 text-[11px] font-semibold tracking-[0.15em] transition-colors duration-300 hover:bg-sage"
          >
            <span className={lang === "en" ? "text-forest" : "text-stone"}>EN</span>
            <span className="text-stone/50">·</span>
            <span className={lang === "fr" ? "text-forest" : "text-stone"}>FR</span>
          </button>
          <a
            href="#contact"
            onClick={go("#contact")}
            data-testid="nav-book-session-btn"
            className="rounded-full bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-moss hover:scale-[1.03] active:scale-95"
          >
            {t.nav.book}
          </a>
        </div>

        <button
          className="text-forest lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-forest/10 bg-cream px-6 pb-6 pt-2 lg:hidden" data-testid="nav-mobile-menu">
          {[...links, { label: t.nav.contact, href: "#contact", testid: "nav-link-contact-mobile" }].map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={l.testid}
              className="block border-b border-forest/5 py-4 font-serif text-2xl italic"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={toggle}
            data-testid="lang-toggle-mobile"
            aria-label="Switch language / Changer de langue"
            className="mt-5 flex items-center gap-1.5 rounded-full border border-forest/20 px-3.5 py-2 text-[11px] font-semibold tracking-[0.15em]"
          >
            <span className={lang === "en" ? "text-forest" : "text-stone"}>EN</span>
            <span className="text-stone/50">·</span>
            <span className={lang === "fr" ? "text-forest" : "text-stone"}>FR</span>
          </button>
        </div>
      )}
    </header>
  );
}
